import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Users,
  Building,
  MapPin,
  Phone,
  Mail,
  Facebook,
  Twitter,
  Instagram,
  Linkedin,
} from 'lucide-react';

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.NEXT_PUBLIC_API_BASE_URL ||
  'https://api.educential.com';

const UKStudyPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);

  // Newsletter state
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);
  const [newsletterStatus, setNewsletterStatus] = useState(null); // { type: 'success' | 'error', message: string }

  const universities = [
    {
      name: 'University of Edinburgh',
      logo:
        'https://images.unsplash.com/photo-1607237138185-eedd9c632b0b?w=120&h=120&fit=crop',
    },
    {
      name: "King's College London",
      logo:
        'https://images.unsplash.com/photo-1564981797816-1043664bf78d?w=120&h=120&fit=crop',
    },
    {
      name: 'University of Manchester',
      logo:
        'https://images.unsplash.com/photo-1590579491624-f98f36d4c763?w=120&h=120&fit=crop',
    },
    {
      name: 'University of Southampton',
      logo:
        'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=120&h=120&fit=crop',
    },
  ];

  const courses = [
    { name: 'LAW' },
    { name: 'MBA' },
    { name: 'BUSINESS' },
    { name: 'ENGINEERING' },
  ];

  const scholarships = [
    {
      name: 'Chevening Scholarships',
      logo:
        'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?w=150&h=80&fit=crop',
      description: "UK government's global scholarship programme",
    },
    {
      name: 'Rhodes Scholarships',
      logo:
        'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?w=150&h=80&fit=crop',
      description: 'Prestigious scholarship to Oxford University',
    },
    {
      name: 'Commonwealth Scholarships',
      logo:
        'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=150&h=80&fit=crop',
      description: 'For students from Commonwealth countries',
    },
    {
      name: 'Erasmus Mundus',
      logo:
        'https://images.unsplash.com/photo-1551836022-d5d88e9218df?w=150&h=80&fit=crop',
      description: 'European Union scholarship programme',
    },
  ];

  const loanProviders = [
    {
      name: 'Netfund',
      logo:
        'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=120&h=60&fit=crop',
    },
    {
      name: 'Lendwise',
      logo:
        'https://images.unsplash.com/photo-1551836022-4c4c79ecde51?w=120&h=60&fit=crop',
    },
    {
      name: 'Prodigy Finance',
      logo:
        'https://images.unsplash.com/photo-1553729459-efe14ef6055d?w=120&h=60&fit=crop',
    },
    {
      name: 'Future Finance',
      logo:
        'https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=120&h=60&fit=crop',
    },
  ];

  const faqs = [
    {
      question: 'Do I need a visa to study in the UK?',
      answer:
        "Yes, most international students need a Student visa (formerly Tier 4) to study in the UK. You'll need to apply for this visa before traveling to the UK.",
    },
    {
      question: 'Can I work while studying in the UK?',
      answer:
        'Yes, Student visa holders can work up to 20 hours per week during term time and full-time during holidays.',
    },
    {
      question: 'What post-graduation options do I have in the UK?',
      answer:
        'The Graduate route allows international students to stay in the UK for 2 years (3 years for PhD graduates) after completing their studies to work or look for work.',
    },
    {
      question: 'Can I bring my dependents with me to the UK?',
      answer:
        "Yes, if you're studying a postgraduate course that's 9 months or longer, or a government-sponsored course, you may be able to bring your partner and children as dependents.",
    },
  ];

  const documents = [
    {
      name: 'CV (UK Format)',
      description: 'Professional CV template tailored for UK standards',
      preview:
        'https://images.unsplash.com/photo-1586953208448-b95a79798f07?w=200&h=280&fit=crop',
    },
    {
      name: 'Statement of Purpose (SOP)',
      description: 'Template for writing compelling personal statements',
      preview:
        'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=200&h=280&fit=crop',
    },
    {
      name: 'Personal Statement',
      description: 'Guide for crafting effective personal statements',
      preview:
        'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=200&h=280&fit=crop',
    },
  ];

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const handleApplicationCTA = () => {
    window.location.href = '/overseas/uk/pathfinder';
  };

  const handleGetStartedCTA = () => {
    window.location.href = '/overseas/uk/pathfinder';
  };

  const handleFinancialAnalysisCTA = () => {
    window.location.href = '/overseas/uk/financial-analysis';
  };

  const handleReadinessAssessmentCTA = () => {
    window.location.href = '/overseas/uk/readiness-assessment';
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) {
      setNewsletterStatus({
        type: 'error',
        message: 'Please enter your email.',
      });
      return;
    }

    try {
      setNewsletterLoading(true);
      setNewsletterStatus(null);

      const res = await fetch(`${API_BASE_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email: newsletterEmail }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(
          data?.message ||
            data?.error ||
            'Unable to subscribe at the moment. Please try again.'
        );
      }

      setNewsletterStatus({
        type: 'success',
        message: 'You have been subscribed successfully 🎉',
      });
      setNewsletterEmail('');
    } catch (err) {
      setNewsletterStatus({
        type: 'error',
        message: err.message || 'Something went wrong. Please try again.',
      });
    } finally {
      setNewsletterLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section
        className="relative h-[700px] bg-cover bg-center"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=1200&h=600&fit=crop')",
        }}
      >
        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <div className="mb-4">
              <span className="text-lg text-yellow-400">Study in</span>
            </div>
            <h1 className="text-5xl font-bold mb-6">
              Study in the United Kingdom
            </h1>
            <p className="text-xl mb-8 leading-relaxed">
              Explore top-tier education, world-class universities, vibrant
              culture, and study work visa opportunities in the UK.
            </p>
            <div className="flex flex-wrap gap-4">
              <button className="bg-white text-gray-800 px-6 py-3 rounded-lg font-medium hover:bg-gray-100"
                onClick={handleReadinessAssessmentCTA}
              >
                Readiness Assessment
              </button>
              <button
                className="bg-yellow-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-yellow-600"
                onClick={handleApplicationCTA}
              >
                Application Assistance
              </button>
              <button className="border-2 border-white text-white px-6 py-3 rounded-lg font-medium hover:bg-white hover:text-gray-800"
                onClick={handleFinancialAnalysisCTA}
              >
                Financial Analysis
              </button>
            </div>
          </div>
          <div className="hidden lg:block ml-auto">
            <div className="bg-white p-6 rounded-lg shadow-xl max-w-md">
              <h3 className="text-xl font-bold mb-4 text-gray-800">
                Study in New Zealand
              </h3>
              <p className="text-gray-600 mb-4">
                Get expert guidance for your study abroad journey.
              </p>
              <button
                className="w-full bg-yellow-500 text-white py-3 rounded-lg font-medium hover:bg-yellow-600"
                onClick={handleApplicationCTA}
              >
                Get Started
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose the UK Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose the UK?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              The UK is home to some of the world's most prestigious
              universities and offers a vibrant, welcoming environment for
              international students. With a wide range of high-quality academic
              programs, your study with impressive work experiences, and
              cultural experiences, the UK remains a top destination for
              ambitious students worldwide.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
                  <Building className="w-8 h-8 text-blue-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Home to Iconic Universities
              </h3>
              <p className="text-gray-600">
                The UK hosts some of the world's top-ranking universities and
                institutions renowned for excellent academic standards,
                cutting-edge research opportunities.
              </p>
            </div>

            <div className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <Users className="w-8 h-8 text-green-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                International Student-Friendly Policies
              </h3>
              <p className="text-gray-600">
                The UK welcomes students from around the world with streamlined
                visa processes and dedicated support services to help students
                every step of the way.
              </p>
            </div>

            <div className="text-center">
              <div className="mb-6">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto">
                  <GraduationCap className="w-8 h-8 text-purple-600" />
                </div>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">
                Post-Study Work Visa Available
              </h3>
              <p className="text-gray-600">
                With the Graduate Route, students can stay in the UK for up to
                2-3 years after graduation to gain valuable work experience in
                their field.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Popular Universities */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Universities in the UK
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {universities.map((university, index) => (
              <div key={index} className="text-center group cursor-pointer">
                <div className="bg-white rounded-full w-32 h-32 mx-auto mb-4 shadow-lg flex items-center justify-center group-hover:shadow-xl transition-shadow duration-300">
                  <img
                    src={university.logo}
                    alt={university.name}
                    className="w-20 h-20 object-contain rounded-full"
                  />
                </div>
                <h3 className="font-medium text-gray-900">
                  {university.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Courses */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Popular Courses
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {courses.map((course, index) => (
              <div key={index} className="text-center group cursor-pointer">
                <div className="bg-white rounded-lg p-8 shadow-lg group-hover:shadow-xl transition-shadow duration-300">
                  <div className="w-12 h-12 mx-auto mb-4 bg-yellow-100 rounded-lg flex items-center justify-center">
                    <GraduationCap className="w-6 h-6 text-yellow-600" />
                  </div>
                  <h3 className="font-bold text-gray-900">{course.name}</h3>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <button className="bg-yellow-500 hover:bg-yellow-600 text-white px-8 py-3 rounded-lg font-medium">
              View All Courses
            </button>
          </div>
        </div>
      </section>

      {/* Financial Aids/Scholarships */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Financials Aids/ Scholarships
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {scholarships.map((scholarship, index) => (
              <div
                key={index}
                className="bg-gray-50 rounded-lg p-6 text-center hover:shadow-lg transition-shadow duration-300"
              >
                <img
                  src={scholarship.logo}
                  alt={scholarship.name}
                  className="w-full h-20 object-contain mb-4"
                />
                <h3 className="font-bold text-gray-900 mb-2">
                  {scholarship.name}
                </h3>
                <p className="text-sm text-gray-600">
                  {scholarship.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education Loans */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Education Loans
            </h2>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {loanProviders.map((provider, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg text-center hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={provider.logo}
                  alt={provider.name}
                  className="w-full h-12 object-contain mb-4"
                />
                <h3 className="font-medium text-gray-900">
                  {provider.name}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-8">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900 mb-8">
                Frequently Asked Questions
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg"
                  >
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-gray-50"
                    >
                      <span className="font-medium text-gray-900">
                        {faq.question}
                      </span>
                      {openFAQ === index ? (
                        <ChevronDown className="w-5 h-5 text-gray-500" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-gray-500" />
                      )}
                    </button>
                    {openFAQ === index && (
                      <div className="px-6 pb-4">
                        <p className="text-gray-600">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div className="hidden lg:block w-80">
              <div className="bg-gray-50 rounded-lg p-6">
                <h3 className="font-bold text-gray-900 mb-4">
                  Still have questions?
                </h3>
                <p className="text-gray-600 mb-4">
                  Get in touch with our expert counselors for personalized
                  guidance.
                </p>
                <button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-3 rounded-lg font-medium">
                  Contact Us
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* UK Study Document Toolkit */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              UK Study Document Toolkit
            </h2>
            <p className="text-lg text-gray-600">
              Download essential documents and templates for your UK study
              application
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {documents.map((doc, index) => (
              <div
                key={index}
                className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <img
                  src={doc.preview}
                  alt={doc.name}
                  className="w-full h-48 object-cover"
                />
                <div className="p-6">
                  <h3 className="font-bold text-gray-900 mb-2">
                    {doc.name}
                  </h3>
                  <p className="text-gray-600 mb-4">{doc.description}</p>
                  <button className="w-full bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded-lg font-medium">
                    Download Sample
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-yellow-600 to-yellow-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Take the Next Step?
          </h2>
          <p className="text-xl text-yellow-100 mb-8 max-w-2xl mx-auto">
            Whether you have more questions or are ready to begin your
            application, we're here to help. Get in touch with us today!
          </p>
          <button
            className="bg-white hover:bg-gray-100 text-yellow-600 font-semibold py-4 px-8 rounded-lg transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
            onClick={handleGetStartedCTA}
          >
            Get Started
          </button>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-12 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h3 className="text-2xl font-bold text-white mb-4">
            Stay Updated on Trends Arising
          </h3>
          <form
            className="max-w-md mx-auto flex flex-col sm:flex-row items-stretch sm:items-center"
            onSubmit={handleNewsletterSubmit}
          >
            <input
              type="email"
              placeholder="Enter your email"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              className="flex-1 px-4 py-3 rounded-l-lg border-0 focus:ring-2 focus:ring-yellow-500 focus:outline-none"
              required
            />
            <button
              type="submit"
              disabled={newsletterLoading}
              className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 rounded-r-lg font-semibold transition-colors duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {newsletterLoading ? 'Subscribing…' : 'Subscribe'}
            </button>
          </form>
          {newsletterStatus && (
            <p
              className={`mt-3 text-sm ${
                newsletterStatus.type === 'success'
                  ? 'text-green-400'
                  : 'text-red-400'
              }`}
            >
              {newsletterStatus.message}
            </p>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <h3 className="text-xl font-bold mb-4">Company</h3>
              <ul className="space-y-2">
                <li>
                  <a href="/about" className="text-gray-300 hover:text-white">
                    About
                  </a>
                </li>
                <li>
                  <a href="/news" className="text-gray-300 hover:text-white">
                    News
                  </a>
                </li>
                <li>
                  <a
                    href="/countries"
                    className="text-gray-300 hover:text-white"
                  >
                    Countries
                  </a>
                </li>
                <li>
                  <a href="/faqs" className="text-gray-300 hover:text-white">
                    FAQs
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">About Us</h3>
              <ul className="space-y-2">
                <li>
                  <a
                    href="/our-story"
                    className="text-gray-300 hover:text-white"
                  >
                    Our Story
                  </a>
                </li>
                <li>
                  <a
                    href="/careers"
                    className="text-gray-300 hover:text-white"
                  >
                    Careers
                  </a>
                </li>
                <li>
                  <a href="/users" className="text-gray-300 hover:text-white">
                    Users
                  </a>
                </li>
              </ul>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4 text-yellow-500">
                Educentral
              </h3>
              <p className="text-gray-300 mb-4">
                Empowering learners to achieve academic and professional success
                with tailored education and tools.
              </p>
              <div className="flex space-x-4">
                <a href="/twitter" className="text-gray-300 hover:text-white">
                  <Twitter className="w-5 h-5" />
                </a>
                <a href="/facebook" className="text-gray-300 hover:text-white">
                  <Facebook className="w-5 h-5" />
                </a>
                <a href="/linkedin" className="text-gray-300 hover:text-white">
                  <Linkedin className="w-5 h-5" />
                </a>
                <a href="/instagram" className="text-gray-300 hover:text-white">
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold mb-4">Contact</h3>
              <div className="space-y-2 text-gray-300">
                <div className="flex items-center space-x-2">
                  <Phone className="w-4 h-4" />
                  <span>+1 (555) 123-4567</span>
                </div>
                <div className="flex items-center space-x-2">
                  <Mail className="w-4 h-4" />
                  <span>info@educentral.com</span>
                </div>
                <div className="flex items-center space-x-2">
                  <MapPin className="w-4 h-4" />
                  <span>New York, NY</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-300">
              © 2024 Educentral. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a
                href="/privacy-policy"
                className="text-gray-300 hover:text-white"
              >
                Privacy Policy
              </a>
              <a
                href="/terms-and-conditions"
                className="text-gray-300 hover:text-white"
              >
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default UKStudyPage;